/**
 * Formats a Date object to YYYY-MM-DD string format
 * @param {Date} date - Date object to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a YYYY-MM-DD date string to a human-readable format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date string (e.g., "Saturday, December 21, 2024")
 */
export const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formats a YYYY-MM-DD date string to a short format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date string (e.g., "December 21, 2024")
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Validates if a string is in YYYY-MM-DD format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid YYYY-MM-DD format
 */
export const isValidYYYYMMDD = (dateString) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  return regex.test(dateString);
};

/**
 * Rating colors - from worst (1) to best (5)
 * 1 = Red (terrible day)
 * 2 = Orange (bad day)
 * 3 = Yellow (neutral day)
 * 4 = Light Green (good day)
 * 5 = Green (amazing day)
 */
export const ratingColors = {
  1: '#ef5350', // Red
  2: '#ff9800', // Orange
  3: '#ffeb3b', // Yellow
  4: '#8bc34a', // Light Green
  5: '#4caf50', // Green
};

/**
 * Rating labels
 */
export const ratingLabels = {
  1: 'Terrible',
  2: 'Bad',
  3: 'Okay',
  4: 'Good',
  5: 'Amazing',
};

/**
 * Gets the color for a rating value
 * @param {number} rating - Rating value (1-5)
 * @returns {string} Hex color code
 */
export const getRatingColor = (rating) => {
  return ratingColors[rating] || ratingColors[3];
};

/**
 * Gets the label for a rating value
 * @param {number} rating - Rating value (1-5)
 * @returns {string} Rating label
 */
export const getRatingLabel = (rating) => {
  return ratingLabels[rating] || ratingLabels[3];
};

export const DRAFT_COLOR = '#9e9e9e';

/**
 * A day entry is a draft when photo, description, or rating is missing.
 */
export const isPhotoDraft = (photo) => {
  if (!photo) return false;
  if (photo.is_draft != null) return photo.is_draft;
  const hasPhoto = Boolean(photo.file_path && String(photo.file_path).trim());
  const hasDescription = Boolean(photo.description && String(photo.description).trim());
  const hasRating = photo.rating != null && photo.rating >= 1 && photo.rating <= 5;
  return !(hasPhoto && hasDescription && hasRating);
};

